import { groupByDays, lodash } from 'helpers';
import { useConvertDateToUserTimezone } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { Divider, Stack, Typography } from 'libs/ui';
import { Colors } from 'libs/ui/theme';

import { VersionType } from '../../types';

import { Version } from './Version/Version';

interface Props<V extends VersionType> {
  versionId: string;
  versions: V[];
  onClickVersion: (version: V) => void;
}

export const VersionList = <V extends VersionType>({
  versions,
  versionId,
  onClickVersion,
}: Props<V>) => {
  const { t, language } = useTranslation();

  const isSelectedVersion = (version: V) => version.id === versionId;

  const { convertDateToUserTimezone } = useConvertDateToUserTimezone();

  const versionsByDays = groupByDays(
    lodash
      .sortBy(versions, ({ createdAt }) => createdAt)
      .reverse()
      .map((v) => ({
        ...v,
        createdAt: convertDateToUserTimezone(v.createdAt),
      })),
    language
  );

  return (
    <Stack spacing={20}>
      <Typography variant="subtitle">
        {t('client.client.versions.title')}
      </Typography>
      <Stack spacing={20}>
        {Object.entries(versionsByDays).map(([day, versions], dayIndex) => (
          <>
            <Stack spacing={16}>
              <Typography color={Colors.gray600} variant="bodyMedium">
                {day}
              </Typography>
              {versions.map((version, index) => (
                <Version
                  key={version.id}
                  isCurrentVersion={index === 0 && dayIndex === 0}
                  isSelectedVersion={isSelectedVersion}
                  version={version}
                  onClickVersion={onClickVersion}
                />
              ))}
            </Stack>
            <Divider />
          </>
        ))}
      </Stack>
    </Stack>
  );
};
